import { Alert, AlertTitle } from '@mui/material'
import React from 'react'

import ResponseLine from 'components/scenarios/create/status-checks/ResponseLine'
import {
  formatPredicateExpected,
  formatPredicateResult
} from 'components/scenarios/utils/criteria-utils'
import { type EvaluationResults } from 'types/api/status-checks/evaluation'
import {
  type EndpointResponse,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { getAWSMonitorName } from 'utils/status-checks/status-check-integrations-util-aws'
import { getDatadogMonitorName } from 'utils/status-checks/status-check-integrations-util-datadog'
import { getDatadogEUMonitorName } from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import { getDatadogUS1FedMonitorName } from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import { getDatadogUS3MonitorName } from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import { getDatadogUS5MonitorName } from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import { getNewRelicApplicationName } from 'utils/status-checks/status-check-integrations-util-newrelic'
import { getPagerdutyServiceName } from 'utils/status-checks/status-check-integrations-util-pagerduty'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const TestEvaluationResultsStatusCheckIntegration: React.FC<{
  endpointResponse: EndpointResponse
  evaluationResults: EvaluationResults
  integration?: TeamExternalIntegration
}> = ({
  endpointResponse,
  evaluationResults: { okStatusCodes, okLatencyMaxMs, responseBodyResults },
  integration
}) => {
  const alertSeverity =
    okStatusCodes?.passed &&
    okLatencyMaxMs?.passed &&
    responseBodyResults?.passed
      ? 'success'
      : 'error'

  return (
    <Alert
      data-test-test-evaluation-results-status-check-integration
      severity={alertSeverity}
    >
      <AlertTitle data-testid="test-evaluation-status-check-integration-alert">
        {createEvaluationResponseMessage(alertSeverity)}
        {integration && createMonitorNameMessage(endpointResponse, integration)}
      </AlertTitle>
      <ResponseLine
        data-testid={`test-evaluation-status-check-integration-alert-status-code-${
          okStatusCodes?.passed ? 'success' : 'error'
        }`}
        fieldName="Status Code"
        formattedActual={okStatusCodes?.actual}
        type={okStatusCodes?.passed ? 'success' : 'error'}
      />
      <ResponseLine
        data-testid={`test-evaluation-status-check-integration-alert-request-timeout-${
          okLatencyMaxMs?.passed ? 'success' : 'error'
        }`}
        fieldName="Request Timeout"
        formattedActual={`${okLatencyMaxMs?.actual} ms`}
        type={okLatencyMaxMs?.passed ? 'success' : 'error'}
      />
      {responseBodyResults?.error && (
        <ResponseLine
          data-testid="test-evaluation-status-check-integration-alert-response-error"
          fieldName="Response Body Criteria"
          formattedActual={responseBodyResults?.error}
          type="error"
        />
      )}
      {responseBodyResults?.predicateResults?.map((predicate, index) => (
        <ResponseLine
          key={index}
          data-testid={`test-evaluation-status-check-integration-alert-response-body-${
            predicate.passed ? 'success' : 'error'
          }`}
          fieldName="Response Body Criteria"
          formattedActual={`${formatPredicateExpected(
            predicate.predicate
          )} (Result: ${formatPredicateResult(predicate)})`}
          type={predicate.passed ? 'success' : 'error'}
        />
      ))}
    </Alert>
  )
}

export const getResourceNameFromResponseBody = (
  endpointResponse: EndpointResponse,
  integration: TeamExternalIntegration
): string | undefined => {
  switch (integration.type) {
    case IntegrationType.Datadog:
      return getDatadogMonitorName(endpointResponse)
    case IntegrationType.DatadogUS3:
      return getDatadogUS3MonitorName(endpointResponse)
    case IntegrationType.DatadogUS5:
      return getDatadogUS5MonitorName(endpointResponse)
    case IntegrationType.DatadogEU:
      return getDatadogEUMonitorName(endpointResponse)
    case IntegrationType.DatadogUS1Fed:
      return getDatadogUS1FedMonitorName(endpointResponse)
    case IntegrationType.Pagerduty:
      return getPagerdutyServiceName(endpointResponse, integration)
    case IntegrationType.Newrelic:
      return getNewRelicApplicationName(endpointResponse, integration)
    case IntegrationType.AWS:
      return getAWSMonitorName(endpointResponse)
  }
}

export const createEvaluationResponseMessage = (
  alertSeverity: string
): string => {
  return alertSeverity === 'error'
    ? 'Evaluation response did not match'
    : 'Evaluation response matched'
}

export const createMonitorNameMessage = (
  endpointResponse: EndpointResponse,
  integration: TeamExternalIntegration
) => {
  if (integration != null) {
    const resourceName = getResourceNameFromResponseBody(
      endpointResponse,
      integration
    )

    if (resourceName != null) {
      return ` for monitor: ${resourceName}`
    }
  }
  return ''
}
